import React, { useEffect, useState } from "react";
import { SpaceUtilization } from "../../../types/api";
import {
  useSpaceUtilizationStore,
  usePremisesStore,
  useDepartmentStore,
} from "../../../store/store";
import { toast } from "react-toastify";
import { useLocationStore } from "../../../store/masterDataStore";

interface AddSpaceUtilizationProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: SpaceUtilization) => void;
  mode: "create" | "edit" | "view";
  initialData?: SpaceUtilization | null;
}

const AddSpaceUtilization: React.FC<AddSpaceUtilizationProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  mode,
}) => {
  const [validationError, setValidationError] = useState<string>("");

  const defaultFormData: SpaceUtilization = {
    premiseId: "",
    areaId: "",
    projectId: "",
    projectName: "",
    utilizationType: "before",
    areaName: "",
    areaSize: 0,
    unit: "sqft",
    utilizationPercentage: undefined,
    purpose: "",
    department: "",
    floor: "",
    capacity: undefined,
    currentOccupancy: undefined,
    notes: "",
    recordedDate: new Date().toISOString().split("T")[0],
    documents: [],
    isActive: true,
  };

  const [formData, setFormData] = useState<SpaceUtilization>(defaultFormData);
  const {
    createSpaceUtilization,
    updateSpaceUtilization,
    loading: storeLoading,
  } = useSpaceUtilizationStore();
  const { allPremises, fetchPremises } = usePremisesStore();
  const { departments, fetchAllDepartments } = useDepartmentStore();
  const { allLocations, fetchAllLocations } = useLocationStore();

  useEffect(() => {
    fetchPremises();
    fetchAllDepartments();
    fetchAllLocations();
  }, [fetchPremises, fetchAllDepartments, fetchAllLocations]);

  useEffect(() => {
    if (initialData && (mode === "edit" || mode === "view")) {
      setFormData({
        ...initialData,
        recordedDate: initialData.recordedDate
          ? new Date(initialData.recordedDate).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
      });
    } else if (mode === "create") {
      setFormData({
        ...defaultFormData,
        recordedDate: new Date().toISOString().split("T")[0],
      });
    }
  }, [initialData, mode, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError("");

    // Validate required fields
    if (!formData.premiseId?.trim()) {
      setValidationError("Premise is required");
      toast.error("Premise is required");
      return;
    }

    if (!formData.areaName?.trim()) {
      setValidationError("Area Name is required");
      toast.error("Area Name is required");
      return;
    }

    if (!formData.areaSize || formData.areaSize <= 0) {
      setValidationError("Area Size must be greater than 0");
      toast.error("Area Size must be greater than 0");
      return;
    }

    if (!formData.utilizationType) {
      setValidationError("Utilization Type is required");
      toast.error("Utilization Type is required");
      return;
    }

    if (
      formData.utilizationPercentage !== undefined &&
      formData.utilizationPercentage !== null
    ) {
      if (
        formData.utilizationPercentage < 0 ||
        formData.utilizationPercentage > 100
      ) {
        setValidationError("Utilization Percentage must be between 0 and 100");
        toast.error("Utilization Percentage must be between 0 and 100");
        return;
      }
    }

    try {
      const submitData: SpaceUtilization = {
        ...formData,
        areaSize: parseFloat(formData.areaSize.toString()),
        utilizationPercentage:
          formData.utilizationPercentage !== undefined &&
          formData.utilizationPercentage !== null
            ? parseFloat(formData.utilizationPercentage.toString())
            : undefined,
        capacity:
          formData.capacity !== undefined && formData.capacity !== null
            ? parseInt(formData.capacity.toString())
            : undefined,
        currentOccupancy:
          formData.currentOccupancy !== undefined &&
          formData.currentOccupancy !== null
            ? parseInt(formData.currentOccupancy.toString())
            : undefined,
        recordedDate: formData.recordedDate
          ? new Date(formData.recordedDate).toISOString()
          : new Date().toISOString(),
      };

      if (mode === "create") {
        await createSpaceUtilization(submitData);
      } else if (mode === "edit" && (initialData?._id || initialData?.id)) {
        await updateSpaceUtilization(
          initialData._id || initialData.id || "",
          submitData
        );
      }

      onSubmit?.(submitData);
      onClose();
    } catch (error) {
      console.error("Error submitting space utilization:", error);
    }
  };

  const handleCancel = () => {
    setFormData(defaultFormData);
    setValidationError("");
    onClose();
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    if (mode === "view") return;
    const { name, value } = e.target;
    setFormData((prev: SpaceUtilization) => ({
      ...prev,
      [name]: value,
    }));
    setValidationError("");
  };

  const handleNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: keyof SpaceUtilization
  ) => {
    if (mode === "view") return;
    const value = e.target.value;
    setFormData((prev: SpaceUtilization) => ({
      ...prev,
      [field]: value === "" ? undefined : parseFloat(value),
    }));
    setValidationError("");
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
    >
      <div className="bg-white w-full max-w-5xl max-h-[90vh] rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            {mode === "edit"
              ? "Edit Space Utilization Record"
              : mode === "view"
              ? "View Space Utilization Record"
              : "Add Space Utilization Record"}
          </h2>
        </div>

        {/* Form Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <form onSubmit={handleSubmit}>
            {validationError && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {validationError}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Premise */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Premise <span className="text-red-500">*</span>
                </label>
                <select
                  name="premiseId"
                  value={formData.premiseId}
                  onChange={handleInputChange}
                  disabled={mode === "view"}
                  required={mode !== "view"}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#93AA9A] focus:border-[#93AA9A] ${
                    mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                >
                  <option value="">Select Premise</option>
                  {allPremises.map(
                    (premise: {
                      _id?: string;
                      id?: string;
                      premisesName: string;
                    }) => (
                      <option
                        key={premise._id || premise.id}
                        value={premise._id || premise.id}
                      >
                        {premise.premisesName}
                      </option>
                    )
                  )}
                </select>
              </div>

              {/* Area/Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Area/Location
                </label>
                <select
                  name="areaId"
                  value={formData.areaId || ""}
                  onChange={handleInputChange}
                  disabled={mode === "view"}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#93AA9A] focus:border-[#93AA9A] ${
                    mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                >
                  <option value="">Select Area/Location</option>
                  {allLocations.map((location) => (
                    <option key={location._id} value={location._id}>
                      {location.room}
                    </option>
                  ))}
                </select>
              </div>

              {/* Utilization Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Utilization Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="utilizationType"
                  value={formData.utilizationType}
                  onChange={handleInputChange}
                  disabled={mode === "view"}
                  required={mode !== "view"}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#93AA9A] focus:border-[#93AA9A] ${
                    mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                >
                  <option value="before">Before Construction/Renovation</option>
                  <option value="after">After Construction/Renovation</option>
                </select>
              </div>

              {/* Area Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Area Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="areaName"
                  value={formData.areaName}
                  onChange={handleInputChange}
                  disabled={mode === "view"}
                  required={mode !== "view"}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#93AA9A] focus:border-[#93AA9A] ${
                    mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                />
              </div>

              {/* Area Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Area Size <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    name="areaSize"
                    value={formData.areaSize || ""}
                    onChange={(e) => handleNumberChange(e, "areaSize")}
                    disabled={mode === "view"}
                    required={mode !== "view"}
                    min="0"
                    step="0.01"
                    className={`flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#93AA9A] focus:border-[#93AA9A] ${
                      mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                    }`}
                  />
                  <select
                    name="unit"
                    value={formData.unit || "sqft"}
                    onChange={handleInputChange}
                    disabled={mode === "view"}
                    className={`w-24 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#93AA9A] focus:border-[#93AA9A] ${
                      mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                    }`}
                  >
                    <option value="sqft">sqft</option>
                    <option value="sqm">sqm</option>
                  </select>
                </div>
              </div>

              {/* Utilization Percentage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Utilization Percentage (%)
                </label>
                <input
                  type="number"
                  name="utilizationPercentage"
                  value={formData.utilizationPercentage || ""}
                  onChange={(e) =>
                    handleNumberChange(e, "utilizationPercentage")
                  }
                  disabled={mode === "view"}
                  min="0"
                  max="100"
                  step="0.1"
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#93AA9A] focus:border-[#93AA9A] ${
                    mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                />
              </div>

              {/* Purpose */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Purpose
                </label>
                <input
                  type="text"
                  name="purpose"
                  value={formData.purpose || ""}
                  onChange={handleInputChange}
                  disabled={mode === "view"}
                  placeholder="e.g., ICU, Ward, Storage, Office"
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#93AA9A] focus:border-[#93AA9A] ${
                    mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                />
              </div>

              {/* Department */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Department
                </label>
                <select
                  name="department"
                  value={formData.department || ""}
                  onChange={handleInputChange}
                  disabled={mode === "view"}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#93AA9A] focus:border-[#93AA9A] ${
                    mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                >
                  <option value="">Select Department</option>
                  {departments.map((dept) => (
                    <option key={dept._id} value={dept.departmentName}>
                      {dept.departmentName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Floor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Floor
                </label>
                <input
                  type="text"
                  name="floor"
                  value={formData.floor || ""}
                  onChange={handleInputChange}
                  disabled={mode === "view"}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#93AA9A] focus:border-[#93AA9A] ${
                    mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                />
              </div>

              {/* Capacity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Capacity
                </label>
                <input
                  type="number"
                  name="capacity"
                  value={formData.capacity || ""}
                  onChange={(e) => handleNumberChange(e, "capacity")}
                  disabled={mode === "view"}
                  min="0"
                  placeholder="e.g., number of beds, seats"
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#93AA9A] focus:border-[#93AA9A] ${
                    mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                />
              </div>

              {/* Current Occupancy */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Occupancy
                </label>
                <input
                  type="number"
                  name="currentOccupancy"
                  value={formData.currentOccupancy || ""}
                  onChange={(e) => handleNumberChange(e, "currentOccupancy")}
                  disabled={mode === "view"}
                  min="0"
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#93AA9A] focus:border-[#93AA9A] ${
                    mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                />
              </div>

              {/* Project Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Name
                </label>
                <input
                  type="text"
                  name="projectName"
                  value={formData.projectName || ""}
                  onChange={handleInputChange}
                  disabled={mode === "view"}
                  placeholder="Construction/Renovation project name"
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#93AA9A] focus:border-[#93AA9A] ${
                    mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                />
              </div>

              {/* Recorded Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recorded Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="recordedDate"
                  value={formData.recordedDate || ""}
                  onChange={handleInputChange}
                  disabled={mode === "view"}
                  required={mode !== "view"}
                  max={new Date().toISOString().split("T")[0]}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#93AA9A] focus:border-[#93AA9A] ${
                    mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                />
              </div>

              {/* Notes */}
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes || ""}
                  onChange={handleInputChange}
                  disabled={mode === "view"}
                  rows={3}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#93AA9A] focus:border-[#93AA9A] ${
                    mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                />
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-2 bg-[#5A7192] w-full rounded-b-lg flex justify-end gap-3">
          <button
            type="button"
            onClick={handleCancel}
            className="flex items-center justify-center font-semibold gap-3 bg-[#869ab6] text-white px-4 py-2 rounded-full hover:bg-[#7589a5] transition-colors"
          >
            CANCEL
          </button>
          {mode === "view" ? null : (
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={storeLoading}
              className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full hover:bg-[#0d8f8c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {storeLoading
                ? "PROCESSING..."
                : mode === "edit"
                ? "UPDATE"
                : "SUBMIT"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddSpaceUtilization;
